import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/properties", label: "Properties" },
    { 
      label: "Markets", 
      children: [
        { path: "/nyc", label: "New York City" },
        { path: "/miami", label: "Miami" }
      ]
    },
    { path: "/process", label: "Process" },
    { path: "/management", label: "Management" },
    { path: "/calculator", label: "Calculator" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navigation */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-white border-b border-gray-100 shadow-sm" 
            : "bg-white/80 backdrop-blur-sm"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2"
              data-testid="logo-link"
            >
              <span className="text-2xl font-bold tracking-tight text-[#1A1A2E]">
                1031<span className="text-[#0066FF]">Condo</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link, idx) => (
                link.children ? (
                  <div key={idx} className="relative group">
                    <button 
                      className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-[#1A1A2E] transition-colors"
                      data-testid={`nav-dropdown-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[160px]">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="block px-4 py-2.5 text-sm text-gray-600 hover:text-[#0066FF] hover:bg-blue-50 transition-colors"
                            data-testid={`nav-link-${child.path.slice(1)}`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === link.path 
                        ? "text-[#0066FF]" 
                        : "text-gray-600 hover:text-[#1A1A2E]"
                    }`}
                    data-testid={`nav-link-${link.path.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/portal"
                className="text-sm font-medium text-gray-600 hover:text-[#0066FF] transition-colors"
                data-testid="nav-portal-btn"
              >
                Client Login
              </Link>
              <Link
                to="/contact"
                className="btn-primary px-6 py-2.5 text-sm font-semibold rounded-lg"
                data-testid="nav-contact-btn"
              >
                Register Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-[#1A1A2E]"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100"
            >
              <div className="px-6 py-4 space-y-3">
                {navLinks.map((link, idx) => (
                  link.children ? (
                    <div key={idx} className="space-y-2">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {link.label}
                      </span>
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block py-2 text-gray-600 hover:text-[#0066FF]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block py-2 text-gray-600 hover:text-[#0066FF]"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
                <Link
                  to="/contact"
                  className="block w-full btn-primary px-6 py-3 text-center text-sm font-semibold rounded-lg mt-4"
                >
                  Register Now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      {/* Footer - Lighter */}
      <footer className="bg-[#1A1A2E] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <span className="text-2xl font-bold tracking-tight">
                1031<span className="text-[#60A5FA]">Condo</span>
              </span>
              <p className="mt-4 text-gray-400 text-sm leading-relaxed">
                Specialized 1031 exchange property sourcing for luxury condo units in New York City and Miami.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">
                Quick Links
              </h4>
              <ul className="space-y-3">
                <li><Link to="/guide" className="text-gray-400 hover:text-white text-sm transition-colors">1031 Exchange Guide</Link></li>
                <li><Link to="/properties" className="text-gray-400 hover:text-white text-sm transition-colors">Available Properties</Link></li>
                <li><Link to="/process" className="text-gray-400 hover:text-white text-sm transition-colors">How It Works</Link></li>
                <li><Link to="/resources" className="text-gray-400 hover:text-white text-sm transition-colors">Resources</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm transition-colors">Contact Us</Link></li>
              </ul>
            </div>

            {/* Markets */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">
                Markets
              </h4>
              <ul className="space-y-3">
                <li><Link to="/nyc" className="text-gray-400 hover:text-white text-sm transition-colors">New York City</Link></li>
                <li><Link to="/miami" className="text-gray-400 hover:text-white text-sm transition-colors">Miami</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">
                Legal
              </h4>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="text-gray-400 hover:text-white text-sm transition-colors">Disclaimer</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} 1031Condo.com. All rights reserved.
              </p>
              <div className="flex gap-4 text-xs text-gray-500">
                <Link to="/privacy" className="hover:text-gray-300">Privacy</Link>
                <Link to="/terms" className="hover:text-gray-300">Terms</Link>
                <Link to="/disclaimer" className="hover:text-gray-300">Disclaimer</Link>
              </div>
            </div>
            <p className="text-gray-500 text-xs text-center md:text-left leading-relaxed mb-2">
              Owned and operated by Krieger Real Estate LLC. Licensed real estate broker in New York (Lic. #10491202190) and Florida (Lic. #CQ1072150).
            </p>
            <p className="text-gray-500 text-xs text-center md:text-left leading-relaxed">
              Krieger Real Estate LLC is a licensed real estate brokerage. This website is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. We do not provide investment, legal, or tax advice. All real estate transactions involve direct property ownership. No brokerage relationship is formed until a written agreement is executed. All real estate purchases involve risk. Buyers should conduct independent due diligence.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
